import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Trash01Icon } from "@untitledui-icons/react/line"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/base/buttons"
import { AlertDialog } from "@/components/ui/AlertDialog"
import { LoadingSpinner } from "@/components/common/LoadingSpinner"
import { api, getErrorMessage } from "@/lib/api"
import { useLead, useDeleteLead, leadKeys } from "@/hooks/api/useSignups"
import type { Lead } from "@/types/signup"

const statusStyles: Record<Lead["status"], { bg: string; text: string }> = {
  DRAFT: {
    bg: "bg-gray-100 dark:bg-gray-800",
    text: "text-gray-700 dark:text-gray-300",
  },
  PENDING: {
    bg: "bg-amber-100 dark:bg-amber-900/40",
    text: "text-amber-700 dark:text-amber-300",
  },
  COMPLETED: {
    bg: "bg-green-100 dark:bg-green-900/40",
    text: "text-green-700 dark:text-green-300",
  },
  ABANDONED: {
    bg: "bg-red-100 dark:bg-red-900/40",
    text: "text-red-700 dark:text-red-300",
  },
}

function StatusBadge({ status }: { status: Lead["status"] }) {
  const style = statusStyles[status] ?? statusStyles.DRAFT
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${style.bg} ${style.text}`}
    >
      {status}
    </span>
  )
}

function timeAgo(date: Date): string {
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

function DateValue({ value }: { value: string }) {
  const date = new Date(value)
  return (
    <span>
      <span className="text-gray-900 dark:text-gray-100">{timeAgo(date)}</span>
      <span className="ml-2 text-gray-400 dark:text-gray-500">{date.toLocaleString()}</span>
    </span>
  )
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3 sm:grid-cols-4">
      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="col-span-2 text-sm text-gray-900 dark:text-gray-100 sm:col-span-3">
        {value || "\u2014"}
      </dd>
    </div>
  )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      <dl className="divide-y divide-gray-100 px-6 dark:divide-gray-800">{children}</dl>
    </div>
  )
}

const PROVISION_BUTTON_CLASS =
  "inline-flex w-44 items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"

function ProvisionButton({ leadId, resourceName, endpoint, onSuccess }: {
  leadId: string
  resourceName: string
  endpoint: string
  onSuccess?: () => void
}) {
  const [state, setState] = useState<"idle" | "loading">("idle")

  const handleProvision = async () => {
    setState("loading")
    try {
      await api.post(`/leads/${leadId}/${endpoint}`)
      toast.success(`${resourceName} created`)
      onSuccess?.()
    } catch (err: unknown) {
      setState("idle")
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(message || `Failed to provision ${resourceName}`)
    }
  }

  return (
    <button
      type="button"
      disabled={state === "loading"}
      onClick={handleProvision}
      className={PROVISION_BUTTON_CLASS}
    >
      {state === "loading" ? "Provisioning..." : `Provision ${resourceName}`}
    </button>
  )
}

const RETRIGGER_COOLDOWN_MS = 2 * 60 * 1000

function ProvisionAssistantButton({ leadId }: { leadId: string }) {
  const storageKey = `provision-assistant-${leadId}`
  const stored = localStorage.getItem(storageKey)
  const storedTs = stored ? parseInt(stored, 10) : null
  const initialDispatched = storedTs && Date.now() - storedTs < RETRIGGER_COOLDOWN_MS

  const [state, setState] = useState<"idle" | "loading" | "dispatched">(initialDispatched ? "dispatched" : "idle")
  const [templateId, setTemplateId] = useState("")
  const [dispatchedAt, setDispatchedAt] = useState<number | null>(storedTs)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    if (state !== "dispatched" || !dispatchedAt) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [state, dispatchedAt])

  const handleProvision = async () => {
    setState("loading")
    try {
      const body = templateId ? { template_id: parseInt(templateId, 10) } : {}
      await api.post(`/leads/${leadId}/provision-assistant`, body)
      const ts = Date.now()
      setState("dispatched")
      setDispatchedAt(ts)
      localStorage.setItem(storageKey, ts.toString())
      toast.success("Assistant provisioning started")
    } catch (err: unknown) {
      setState("idle")
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(message || "Failed to provision Assistant")
    }
  }

  const cooldownRemaining = dispatchedAt ? Math.max(0, RETRIGGER_COOLDOWN_MS - (now - dispatchedAt)) : 0
  const canRetrigger = cooldownRemaining === 0

  if (state === "dispatched") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs italic text-gray-400 dark:text-gray-500">
          Provisioning in progress — this can take a minute
        </span>
        {canRetrigger ? (
          <button
            type="button"
            onClick={() => setState("idle")}
            className="text-xs text-gray-400 underline hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            Re-trigger
          </button>
        ) : (
          <span className="text-xs text-gray-300 dark:text-gray-600">
            Re-trigger in {Math.ceil(cooldownRemaining / 1000)}s
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={templateId}
        onChange={(e) => setTemplateId(e.target.value)}
        placeholder="Template ID"
        className="w-24 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
      />
      <button
        type="button"
        disabled={state === "loading"}
        onClick={handleProvision}
        className={PROVISION_BUTTON_CLASS}
      >
        {state === "loading" ? "Provisioning..." : "Provision Assistant"}
      </button>
    </div>
  )
}


function LinkedResource({ leadId, id, href, resourceName, endpoint, onSuccess }: {
  leadId: string
  id: number | null
  href: string
  resourceName: string
  endpoint: string
  onSuccess?: () => void
}) {
  if (id) {
    return (
      <Link to={href} className="text-brand-600 hover:underline dark:text-brand-400">
        #{id}
      </Link>
    )
  }
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400 dark:text-gray-500">{"\u2014"}</span>
      <ProvisionButton leadId={leadId} resourceName={resourceName} endpoint={endpoint} onSuccess={onSuccess} />
    </div>
  )
}

export function SignupDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: lead, isLoading } = useLead(id)
  const deleteLead = useDeleteLead()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const invalidateLead = () => {
    queryClient.invalidateQueries({ queryKey: leadKeys.detail(id ?? "") })
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteLead.mutateAsync(id)
      toast.success(t("resources.signups.delete_success"))
      navigate("/signups")
    } catch (error: unknown) {
      // Surfaces the backend `detail` message, e.g. the 409 guard for
      // leads linked to a provisioned customer/user/assistant
      toast.error(getErrorMessage(error) || t("resources.signups.delete_error"))
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400">Lead not found</p>
        <Button color="secondary" onPress={() => navigate("/signups")} className="mt-4">
          Back to Signups
        </Button>
      </div>
    )
  }

  const data = lead.data
  // Mirrors the backend 409 guard: provisioned leads cannot be deleted
  const isProvisioned = !!(lead.customer_id || lead.user_id || lead.assistant_id)

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={data?.business_name || "Untitled Lead"}
        subtitle={`Lead ${lead.id}`}
        backButton={{ label: "Back to Signups", onClick: () => navigate("/signups") }}
        actions={
          <div
            title={isProvisioned ? t("resources.signups.delete_disabled_hint") : undefined}
          >
            <Button
              color="secondary-destructive"
              iconLeading={Trash01Icon}
              onPress={() => setShowDeleteDialog(true)}
              isDisabled={isProvisioned || deleteLead.isPending}
            >
              {t("resources.signups.delete_title")}
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-3xl space-y-6">
          {/* Status & Provisioning */}
          <SectionCard title="Status & Actions">
            <DetailField label="Status" value={<StatusBadge status={lead.status} />} />
            {(lead.status === "DRAFT" || lead.status === "ABANDONED") && (
              <DetailField
                label="Payment Link"
                value={
                  <button
                    type="button"
                    onClick={() => {
                      const url = lead.stripe_customer_id
                        ? `${import.meta.env.VITE_API_URL}/public/card-setup/${lead.id}`
                        : `${window.location.origin}/signup/payment?lead_id=${lead.id}`
                      navigator.clipboard.writeText(url)
                      toast.success("Payment link copied to clipboard")
                    }}
                    className={PROVISION_BUTTON_CLASS}
                  >
                    Copy Payment Link
                  </button>
                }
              />
            )}
            <DetailField
              label="Customer"
              value={
                <LinkedResource leadId={lead.id} id={lead.customer_id} href={`/customers/${lead.customer_id}`} resourceName="Customer" endpoint="provision-customer" onSuccess={invalidateLead} />
              }
            />
            <DetailField
              label="User"
              value={
                <LinkedResource leadId={lead.id} id={lead.user_id} href={`/users/${lead.user_id}`} resourceName="User" endpoint="provision-user" onSuccess={invalidateLead} />
              }
            />
            <DetailField
              label="Assistant"
              value={
                lead.assistant_id ? (
                  <Link to={`/assistants/${lead.assistant_id}/about`} className="text-brand-600 hover:underline dark:text-brand-400">
                    #{lead.assistant_id}
                  </Link>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 dark:text-gray-500">{"\u2014"}</span>
                    <ProvisionAssistantButton leadId={lead.id} />
                  </div>
                )
              }
            />
            <DetailField
              label="HubSpot"
              value={
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 dark:text-gray-500">{"\u2014"}</span>
                  <button
                    type="button"
                    onClick={() => toast.info("Link HubSpot is not implemented yet.")}
                    className="inline-flex w-44 items-center rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Link HubSpot
                  </button>
                </div>
              }
            />
            <DetailField
              label="Created"
              value={<DateValue value={lead.created_at} />}
            />
            <DetailField
              label="Updated"
              value={lead.updated_at ? <DateValue value={lead.updated_at} /> : null}
            />
            <DetailField
              label="Completed"
              value={lead.completed_at ? <DateValue value={lead.completed_at} /> : null}
            />
            <DetailField
              label="Abandoned Event Sent"
              value={lead.abandoned_event_sent_at ? <DateValue value={lead.abandoned_event_sent_at} /> : null}
            />
          </SectionCard>

          {/* Contact Info */}
          <SectionCard title="Contact">
            <DetailField label="Email" value={lead.email} />
            <DetailField label="Phone" value={lead.phone_number} />
            <DetailField label="Phone Verified" value={lead.phone_verified ? "Yes" : "No"} />
            <DetailField label="First Name" value={data?.contact_first_name} />
            <DetailField label="Last Name" value={data?.contact_last_name} />
          </SectionCard>

          {/* Business Info */}
          <SectionCard title="Business">
            <DetailField label="Name" value={data?.business_name} />
            <DetailField label="Type" value={data?.business_type} />
            <DetailField label="Phone" value={data?.business_phone} />
            <DetailField label="Address" value={data?.business_address} />
            <DetailField
              label="Website"
              value={
                data?.website ? (
                  <a href={data.website} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline dark:text-brand-400">
                    {data.website}
                  </a>
                ) : null
              }
            />
            <DetailField label="Primary Language" value={data?.primary_language} />
            <DetailField
              label="Additional Languages"
              value={data?.additional_languages?.join(", ")}
            />
            <DetailField
              label="Use Same Phone for Events"
              value={data?.use_same_phone_for_events ? "Yes" : "No"}
            />
          </SectionCard>

          {/* Booking System */}
          <SectionCard title="Booking System">
            <DetailField label="System" value={data?.booking_system} />
            <DetailField label="Booking Validated" value={lead.booking_validated ? "Yes" : "No"} />
            <DetailField label="Restaurant ID" value={data?.booking_restaurant_id} />
            <DetailField label="API Key" value={data?.booking_api_key ? "••••••••" : null} />
            <DetailField label="Partner ID" value={data?.booking_partner_id} />
            <DetailField label="OpenTable RID" value={data?.booking_opentable_rid} />
            <DetailField label="Detected System" value={data?.detected_booking_system} />
            <DetailField label="Detected Restaurant ID" value={data?.detected_restaurant_id} />
            <DetailField label="Detection Status" value={data?.booking_detection_status} />
          </SectionCard>

          {/* Technical */}
          <SectionCard title="Technical">
            <DetailField label="Lead ID" value={lead.id} />
            <DetailField label="Google Place ID" value={lead.google_place_id} />
            <DetailField label="Stripe Customer ID" value={lead.stripe_customer_id} />
            <DetailField label="Stripe Payment Intent" value={lead.stripe_payment_intent_id} />
          </SectionCard>

          {/* Billing */}
          <SectionCard title="Billing">
            <DetailField label="Company" value={data?.company_name} />
            <DetailField label="Email" value={data?.billing_email} />
            <DetailField label="Address" value={data?.billing_address} />
            <DetailField label="Address Line 2" value={data?.billing_address_line2} />
            <DetailField label="City" value={data?.billing_city} />
            <DetailField label="State" value={data?.billing_state} />
            <DetailField label="Postal Code" value={data?.billing_postal_code} />
            <DetailField label="Country" value={data?.billing_country} />
            <DetailField label="VAT Number" value={data?.vat_number} />
            <DetailField label="Tax ID Type" value={data?.tax_id_type} />
            <DetailField label="Tax ID Value" value={data?.tax_id_value} />
          </SectionCard>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t("resources.signups.delete_title")}
        description={t("resources.signups.delete_description", {
          name: data?.business_name || lead.email || lead.id,
        })}
        confirmLabel={t("resources.generic.delete")}
        variant="error"
        isLoading={deleteLead.isPending}
      />
    </div>
  )
}

export default SignupDetailPage
